import { SortOrder } from "@lxc/app-device-common";
import { storeToRefs } from "pinia";
import { useAcl } from "vue-simple-acl";
import { useFetchPageV2 } from "~/core/composables/useFetchPageV2";
import { useSearch } from "~/core/composables/useSearch";
import { useSort } from "~/core/composables/useSort";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import type LxcError from "~/core/models/LxcError.class";
import type { UseFetchParameters } from "~/core/models/UseFetchParameters.interface";
import { fetchAllPaginatedList } from "~/core/utils/fetch";
import filtersUtils from "~/core/utils/filters.utils";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { DtwinColumns } from "~/modules/dtwin/models/DtwinColumns.enum";
import dtwinsService from "~/modules/dtwin/services/dtwin.service";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import type { SelectedItemType } from "~/modules/sector/models/SelectedItem.type";
import type { SelectedItemsType } from "~/modules/sector/models/SelectedItems.type";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import {
  CUSTOM_IN_FLEET_RSQL_OPERATOR,
  CUSTOM_OUT_FLEET_RSQL_OPERATOR,
  Filters,
  type FiltersSelection,
  FiltersType,
  OPERATOR_RSQL_ILIKE,
  OPERATOR_RSQL_IN,
  OPERATOR_RSQL_NOT_NULL,
  OPERATOR_RSQL_OUT,
  SectorResource,
} from "~/types";

const { can } = useAcl();

interface UseFetchDtwinsParameters extends UseFetchParameters {
  outSector?: boolean;
}

export function useFetchDtwins({
  organizationCode,
  sectorCode,
  searchMode,
  useQueryParametersForPagination,
  outSector,
}: UseFetchDtwinsParameters) {
  const itemType = SectorResource.DTWINS;

  let service = dtwinsService.getOrganizationDtwins.bind(dtwinsService);

  if (sectorCode && !outSector) {
    service = dtwinsService.getOrganizationSectorDtwins.bind(dtwinsService);
  }

  const appliedFilters: FiltersSelection = new Map([
    [
      Filters.NAME,
      {
        key: ["attributes.friendlyName"],
        operator: OPERATOR_RSQL_ILIKE,
        value: "",
      },
    ],
    [
      Filters.DTWIN_NAME_OR_SERIAL_NUMBER,
      {
        key: ["attributes.friendlyName", "attributes.serialNumber"],
        operator: OPERATOR_RSQL_ILIKE,
        value: "",
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
    [
      Filters.DTWINS_IN_LIFE_CYCLE_STATE,
      { key: "lifeCycleState", operator: OPERATOR_RSQL_IN, value: [] },
    ],
    [
      Filters.SECTORS,
      { key: "sectorCode", operator: OPERATOR_RSQL_IN, value: [] },
    ],
    [
      Filters.SECTORS_EXCLUDED,
      { key: "sectorCode", operator: OPERATOR_RSQL_OUT, value: [] },
    ],
    [
      Filters.DTWIN_MODEL_TYPE,
      { key: "deviceModelUid", operator: OPERATOR_RSQL_IN, value: [] },
    ],
    [
      Filters.DTWINS_IN_FLEET,
      { key: "", operator: CUSTOM_IN_FLEET_RSQL_OPERATOR, value: "" },
    ],
    [
      Filters.DTWINS_NOT_IN_FLEET,
      { key: "", operator: CUSTOM_OUT_FLEET_RSQL_OPERATOR, value: "" },
    ],
    [Filters.DTWIN_UID, { key: "uid", operator: OPERATOR_RSQL_IN, value: [] }],
    [
      Filters.DTWIN_UID_OUT,
      { key: "uid", operator: OPERATOR_RSQL_OUT, value: [] },
    ],
    [
      Filters.DTWIN_IN_SOFTWARE_VERSION,
      {
        key: "features.firmware.reported.firmwareVersion",
        operator: OPERATOR_RSQL_IN,
        value: [],
      },
    ],
    [
      Filters.DTWIN_IN_HARDWARE_VERSION,
      {
        key: "attributes.hardwareVersion",
        operator: OPERATOR_RSQL_IN,
        value: [],
      },
    ],
    [
      Filters.DTWIN_HARDWARE_VERSION_NOT_NULL,
      {
        key: "attributes.hardwareVersion",
        operator: OPERATOR_RSQL_NOT_NULL,
        value: [],
      },
    ],
    [
      Filters.DTWIN_CONNECTIVITY_STATUS,
      {
        key: "connectivityStatus",
        operator: OPERATOR_RSQL_IN,
        value: [],
      },
    ],
    [
      Filters.DTWIN_RANGE,
      {
        key: "attributes.range",
        operator: OPERATOR_RSQL_IN,
        value: [],
      },
    ],
  ]);

  const { searchParams, filters, setFilter, search, onSearch } = useSearch(
    FiltersType.RSQL,
    appliedFilters,
    searchMode,
  );
  const { sort, onSortChange } = useSort({
    prop: DtwinColumns.CREATED_AT,
    order: SortOrder.DESC,
  });
  const { isLoading, results, allResults, error, fetchData, fetchAllData } =
    useFetchPageV2({
      organizationCode,
      sectorCode,
      service,
      searchParams,
      sort,
      useQueryParametersForPagination,
    });

  function canManageItems(sector: Sector): boolean {
    const { availableSectors } = storeToRefs(useSectorStore());
    return (
      can([ACLRoles.CAN_READ_SECTOR, ACLRoles.CAN_WRITE_DEVICE]) &&
      availableSectors.value
        .map((availableSector) => availableSector.code)
        .includes(sector.code)
    );
  }

  function getDescription(dtwin?: SelectedItemType) {
    return (dtwin as Dtwin)?.attributes.friendlyName ?? "";
  }

  function getItemIds(items: SelectedItemsType): string[] {
    return items.map((item) => (item as Dtwin).uid);
  }

  /**
   * Execute the search with filters set and the provided sectors
   * @param selectedSectors
   * @param replaceHistory
   */
  async function searchOnSectors(
    selectedSectors: Sector[],
    replaceHistory?: boolean,
  ) {
    // set filter on selected sectors
    setFilter(
      Filters.SECTORS,
      selectedSectors.map((sector) => sector.code),
    );

    // call search in order to update the query parameters with filters
    await search(replaceHistory);
  }

  /**
   * Retrieve all the devices
   * @param selectedSectors filter on sectors, optional
   */
  async function fetchAllDtwins(
    selectedSectors?: Sector[],
  ): Promise<Dtwin[] | LxcError> {
    if (selectedSectors) {
      setFilter(
        Filters.SECTORS,
        selectedSectors.map((sector) => sector.code),
      );
    }

    return await fetchAllPaginatedList((page: number, pageSize: number) =>
      dtwinsService.getOrganizationDtwins({
        page,
        pageSize,
        searchParams: searchParams.value,
        organizationCode,
      }),
    );
  }

  return {
    itemType,
    isLoading,
    results,
    allResults,
    error,
    filters,
    fetchData,
    fetchAllData,
    appliedFilters,
    setFilter,
    onSortChange,
    onSearch,
    search,
    searchParams,
    getDescription,
    canManageItems,
    getItemIds,
    fetchAllDtwins,
    searchOnSectors,
  };
}
